/**
 * An aircraft's own photo in Teslyn Cloud (MB's decision 598, Q523 B; docs/specs/cloud/aircraft-photo.md; help
 * `cloud.aircraft`). Tenant admins, managers and office staff set it from a project photo already in Teslyn (its display size is
 * copied: "Use as aircraft photo" on the photo, "Choose from photos" on the aircraft) or by uploading one (drawn as
 * the display size, 2048 px JPEG, as a project photo added here). The JPEG goes to the bucket `aircraft-photos` at a
 * new name each time, then `aircraft.cloud_photo_path` names it; the database (`aircraft_photo_guard` and the
 * bucket's policies, 20261026000200) refuses anyone else and a path that is not that aircraft's stored object. The
 * aircraft row syncs to Teslyn Field, which keeps a copy for the daily and weekly reports.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  AIRCRAFT_PHOTO_BUCKET,
  AIRCRAFT_PHOTO_MAX_BYTES,
  PHOTO_BUCKET,
  aircraftPhotoObjectPath,
  photoFileCheck,
  photoObjectPath,
  type Aircraft,
  type PhotoCategory,
} from '@teslyn/domain';
import type { PhotoRenderer } from '../portal/photoImages.ts';

export const AIRCRAFT_PHOTO_LINK_SECONDS = 300;

/** The columns of an aircraft row the register reads (also `loadAircraft`). */
export const AIRCRAFT_COLUMNS =
  'id, tenant_id, registration, type_name, fuel_flow_lph, mr_interval_h, ttis_at_issue_h, ttis_at_start_h, photo_path, cloud_photo_path, legacy_json, fuel_type, max_flight_h, fuel_capacity_l, engines, engine_starts_tracked, oil_uplift_tracked, fuel_unit, airframe, empty_weight_kg, mtow_kg, reserve_min, altimeter_cals, oil_dipstick_unit, uses_flight_watch, created_at, updated_at, updated_by, deleted_at';

const REFUSED = 'Only a tenant admin or a manager may change the photo of an aircraft.';

/** Database and Storage errors in words: the database's own sentence where it gives one. */
function words(e: { readonly message: string; readonly code?: string }): string {
  if (e.code === '42501' || /row-level security|permission denied/i.test(e.message)) {
    return /Only a tenant admin or a manager/.test(e.message) ? e.message : REFUSED;
  }
  return e.message;
}

/** A signed address (5 minutes) for an aircraft's photo, or null when it cannot be read. */
export async function aircraftPhotoUrl(sb: SupabaseClient, object: string): Promise<string | null> {
  const { data, error } = await sb.storage
    .from(AIRCRAFT_PHOTO_BUCKET)
    .createSignedUrl(object, AIRCRAFT_PHOTO_LINK_SECONDS);
  return error || !data ? null : data.signedUrl;
}

/** Signed addresses for several aircraft photos, by object name; failures left out. */
export async function aircraftPhotoUrls(
  sb: SupabaseClient,
  objects: readonly string[],
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (objects.length === 0) return out;
  const { data, error } = await sb.storage
    .from(AIRCRAFT_PHOTO_BUCKET)
    .createSignedUrls([...objects], AIRCRAFT_PHOTO_LINK_SECONDS);
  if (error) return out;
  for (const d of data ?? []) if (d.path && d.signedUrl && !d.error) out.set(d.path, d.signedUrl);
  return out;
}

const newUuid = (): string => globalThis.crypto.randomUUID();

/** Store `jpeg` as the aircraft's photo and point the aircraft at it; returns the saved aircraft. */
export async function storeAircraftPhoto(
  sb: SupabaseClient,
  aircraft: Pick<Aircraft, 'id' | 'tenant_id' | 'registration'>,
  jpeg: Blob,
  newId: () => string = newUuid,
): Promise<Aircraft> {
  if (jpeg.size > AIRCRAFT_PHOTO_MAX_BYTES)
    throw new Error(`The photo of ${aircraft.registration} is larger than 10 MB.`);
  const object = aircraftPhotoObjectPath(aircraft.tenant_id, aircraft.id, newId());
  const up = await sb.storage
    .from(AIRCRAFT_PHOTO_BUCKET)
    .upload(object, jpeg, { contentType: 'image/jpeg', upsert: false });
  if (up.error) throw new Error(`The photo was not stored: ${words(up.error)}`);
  const r = await sb
    .from('aircraft')
    .update({ cloud_photo_path: object })
    .eq('id', aircraft.id)
    .select(AIRCRAFT_COLUMNS);
  if (r.error) throw new Error(`The photo was not set: ${words(r.error)}`);
  const saved = (r.data ?? [])[0] as Aircraft | undefined;
  if (!saved) throw new Error(REFUSED);
  return saved;
}

/** Upload a photo file as the aircraft's photo: checked as a project photo, drawn at the display size. */
export async function setAircraftPhotoFromFile(
  sb: SupabaseClient,
  aircraft: Pick<Aircraft, 'id' | 'tenant_id' | 'registration'>,
  file: File,
  renderer: PhotoRenderer,
  newId?: () => string,
): Promise<Aircraft> {
  const check = photoFileCheck(file.name, file.size);
  if (!check.ok) throw new Error(check.message);
  const drawn = await renderer.render(file);
  return storeAircraftPhoto(sb, aircraft, drawn.display, newId);
}

/** A project photo the aircraft's photo can be chosen from: live, with its display size in Teslyn Cloud. */
export interface PhotoChoice {
  readonly id: string;
  readonly tenant_id: string;
  readonly project_id: string;
  readonly project_number: string;
  readonly file_name: string;
  readonly caption: string;
  readonly category: PhotoCategory | string;
  readonly created_at: string;
  /** The display size's object in `project-photos`. */
  readonly display: string;
  /** The thumbnail's object in `project-photos`. */
  readonly thumb: string;
}

type PhotoRow = Omit<PhotoChoice, 'project_number' | 'display' | 'thumb'> & { readonly file_ext: string };

/** The organisation's live project photos with their display size in Teslyn Cloud, newest first (at most 200). */
export async function loadPhotoChoices(sb: SupabaseClient, tenantId: string): Promise<PhotoChoice[]> {
  const rows = await sb
    .from('project_photo')
    .select('id, tenant_id, project_id, file_name, file_ext, caption, category, created_at')
    .eq('tenant_id', tenantId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(200);
  if (rows.error) throw new Error(`The photos could not be read: ${rows.error.message}`);
  const photos = (rows.data ?? []) as PhotoRow[];
  if (photos.length === 0) return [];
  const [files, projects] = await Promise.all([
    sb
      .from('project_file')
      .select('record_id')
      .eq('kind', 'photo_display')
      .in(
        'record_id',
        photos.map((p) => p.id),
      ),
    sb.from('project').select('id, number').eq('tenant_id', tenantId),
  ]);
  if (files.error) throw new Error(`The photos could not be read: ${files.error.message}`);
  const withDisplay = new Set(((files.data ?? []) as { record_id: string }[]).map((f) => f.record_id));
  const numbers = new Map(
    ((projects.data ?? []) as { id: string; number: string }[]).map((p) => [p.id, p.number]),
  );
  return photos
    .filter((p) => withDisplay.has(p.id))
    .map(({ file_ext, ...p }) => ({
      ...p,
      project_number: numbers.get(p.project_id) ?? '',
      display: photoObjectPath(p.tenant_id, p.project_id, p.id, 'display', file_ext),
      thumb: photoObjectPath(p.tenant_id, p.project_id, p.id, 'thumb', file_ext),
    }));
}

/** Use a project photo as the aircraft's photo: its display size (2048 px JPEG) is copied to the aircraft. */
export async function setAircraftPhotoFromProjectPhoto(
  sb: SupabaseClient,
  aircraft: Pick<Aircraft, 'id' | 'tenant_id' | 'registration'>,
  photo: Pick<PhotoChoice, 'display'>,
  newId?: () => string,
): Promise<Aircraft> {
  const { data, error } = await sb.storage.from(PHOTO_BUCKET).download(photo.display);
  if (error || !data) throw new Error('The photo could not be read from Teslyn Cloud. Try again.');
  return storeAircraftPhoto(sb, aircraft, data, newId);
}

/** Remove the aircraft's own photo: the reports use the aircraft-type photo again. The file is kept. */
export async function removeAircraftPhoto(
  sb: SupabaseClient,
  aircraft: Pick<Aircraft, 'id'>,
): Promise<Aircraft> {
  const r = await sb
    .from('aircraft')
    .update({ cloud_photo_path: null })
    .eq('id', aircraft.id)
    .select(AIRCRAFT_COLUMNS);
  if (r.error) throw new Error(`The photo was not removed: ${words(r.error)}`);
  const saved = (r.data ?? [])[0] as Aircraft | undefined;
  if (!saved) throw new Error(REFUSED);
  return saved;
}

/**
 * Make a project photo the weekly report's cover, or stop (`cover` false) (decisions 665 and 712):
 * `project_photo_weekly_cover` checks the role and the photo (live, Aircraft category, display size uploaded), and
 * moves the cover from any other photo of the project, whichever aircraft it shows. Refused in the database's words.
 */
export async function setWeeklyCover(sb: SupabaseClient, photoId: string, cover: boolean): Promise<void> {
  const r = await sb.rpc('project_photo_weekly_cover', { p_photo: photoId, p_use: cover });
  if (r.error) throw new Error(`Not changed. ${r.error.message}`);
}
